import { useEffect, useRef } from 'react';
import { useSupabaseClient } from './use-supabase';

/**
 * Records a row in `user_page_visits` for the current route. Fire-and-
 * forget — failures are swallowed silently because visit-tracking is
 * analytics, not user-facing.
 *
 * Throttled to one insert per (user, org, route) per 60 seconds via an
 * in-memory map. Including user+org in the throttle key is important:
 * a shared browser tab that switches users (or a single user that
 * switches orgs) within 60s of visiting the same path would otherwise
 * have the second visit suppressed.
 *
 * Page navigations within the throttle window are dropped — the
 * materialized view computes "frequently visited" from the visit log,
 * and an unthrottled insert on every component re-mount would skew
 * the count.
 *
 * Callers wire this via TanStack Router's onLoad / route mount effect.
 * The hook handles its own mount/unmount semantics.
 */

const lastVisitByKey = new Map<string, number>();
const THROTTLE_MS = 60_000;

export interface PageVisitInput {
  orgId: string | null;
  route: string;
  pageTitle: string;
  moduleSlug?: string | null;
  workspaceSlug?: string | null;
}

export function usePageVisit(input: PageVisitInput): void {
  const supabase = useSupabaseClient();
  const { orgId, route, pageTitle, moduleSlug, workspaceSlug } = input;
  const lastFiredRef = useRef<string | null>(null);

  useEffect(() => {
    if (!orgId || !route) return;

    void (async () => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        const userId = userData.user?.id;
        if (!userId) return;

        // (user, org, route) is the natural throttle key. The
        // mount-dedupe via lastFiredRef also incorporates user+org
        // so a sign-out + sign-in to the same path within 60s logs
        // as a fresh visit for the new identity.
        const throttleKey = `${userId} ${orgId} ${route}`;
        if (lastFiredRef.current === throttleKey) return; // StrictMode double-fire
        lastFiredRef.current = throttleKey;

        const now = Date.now();
        const prior = lastVisitByKey.get(throttleKey) ?? 0;
        if (now - prior < THROTTLE_MS) return;
        lastVisitByKey.set(throttleKey, now);

        await supabase.from('user_page_visits').insert({
          user_id: userId,
          org_id: orgId,
          route,
          module_slug: moduleSlug ?? null,
          workspace_slug: workspaceSlug ?? null,
          page_title: pageTitle,
        });
      } catch {
        // Swallow. Visit tracking is analytics; never user-facing.
      }
    })();
  }, [supabase, orgId, route, pageTitle, moduleSlug, workspaceSlug]);
}
